/**
 * FormDialog — wraps DS Dialog for the title + form-body + submit/cancel pattern.
 *
 * Covers forms that live inside a modal: create/edit/rename workflows.
 *
 * Usage:
 *   <FormDialog
 *     open={showEdit}
 *     onClose={() => setShowEdit(false)}
 *     onSubmit={handleSubmit}
 *     title="Create folder"
 *     submitLabel="Create"
 *     submitting={isCreating}
 *   >
 *     <Input label="Folder name" value={name} onChange={setName} />
 *   </FormDialog>
 */

import React from 'react'
import styled from 'styled-components'
import {Dialog as DSDialog, DialogContent, DialogActions} from '~/src/design-system'
import {Button} from '~/src/design-system/Primitives'
import {X} from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface FormDialogProps {
  /** Controls dialog visibility */
  open: boolean
  /** Called when the dialog requests to close (cancel button, X, ESC, backdrop) */
  onClose: () => void
  /** Called when the submit button is clicked */
  onSubmit: () => void
  /** Dialog title text */
  title: string
  /** Form field content */
  children: React.ReactNode
  /** Submit button label. Default: "Submit" */
  submitLabel?: string
  /** Cancel button label. Default: "Cancel" */
  cancelLabel?: string
  /** Disables submit button and shows loading spinner */
  submitting?: boolean
  /** Disables submit button without loading indicator */
  submitDisabled?: boolean
  /** Max width of the dialog paper. Default: 600px */
  maxWidth?: string
  /** Optional data-testid on the root dialog element */
  'data-testid'?: string
  /** Override testid for the close button (default: `${testId}-close-button`) */
  closeButtonTestId?: string
  /** Override testid for the cancel button (default: `${testId}-cancel-button`) */
  cancelButtonTestId?: string
  /** Override testid for the submit button (default: `${testId}-submit-button`) */
  submitButtonTestId?: string
}

/* ------------------------------------------------------------------ */
/*  Styled overrides                                                   */
/* ------------------------------------------------------------------ */

const SizedDialog = styled(DSDialog)<{$maxWidth: string}>`
  .MuiDialog-paper {
    max-width: ${({$maxWidth}) => $maxWidth};
  }
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-5) var(--space-6) var(--space-4);
  border-bottom: 1px solid var(--border-default, #e5e7eb);
`

const Title = styled.h2`
  margin: 0;
  font-size: var(--fs-lg, 1.25rem);
  font-weight: var(--fw-semibold, 600);
  line-height: var(--lh-tight, 1.25);
  color: var(--text-primary, #111827);
`

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export const FormDialog: React.FC<FormDialogProps> = ({
  open,
  onClose,
  onSubmit,
  title,
  children,
  submitLabel = 'Submit',
  cancelLabel = 'Cancel',
  submitting = false,
  submitDisabled = false,
  maxWidth = '600px',
  'data-testid': testId,
  closeButtonTestId,
  cancelButtonTestId,
  submitButtonTestId,
}) => {
  return (
    <SizedDialog
      open={open}
      onClose={(_e, _reason) => onClose()}
      $maxWidth={maxWidth}
      aria-labelledby="form-dialog-title"
      data-testid={testId}>
      <Header>
        <Title id="form-dialog-title">{title}</Title>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          aria-label="Close dialog"
          data-testid={closeButtonTestId ?? (testId ? `${testId}-close-button` : undefined)}
          iconLeft={<X size={18} />}
        />
      </Header>

      <DialogContent>{children}</DialogContent>

      <DialogActions>
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={submitting}
          data-testid={cancelButtonTestId ?? (testId ? `${testId}-cancel-button` : undefined)}>
          {cancelLabel}
        </Button>
        <Button
          variant="primary"
          onClick={onSubmit}
          loading={submitting}
          disabled={submitting || submitDisabled}
          data-testid={submitButtonTestId ?? (testId ? `${testId}-submit-button` : undefined)}>
          {submitLabel}
        </Button>
      </DialogActions>
    </SizedDialog>
  )
}

export default FormDialog
